import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { FileText, Users, FolderKanban, ClipboardList, LogOut, LayoutDashboard, Link2, ShieldCheck } from "lucide-react";

type GuardState = "loading" | "noauth" | "nomfa" | "norole" | "error" | "ok";

const AdminGuard = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<GuardState>("loading");
  const location = useLocation();
  const cancelled = useRef(false);
  const checking = useRef(false);

  const check = useCallback(async (silent = false) => {
    if (checking.current) return;
    checking.current = true;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        if (!cancelled.current) setState("noauth");
        return;
      }

      const { data: role, error: roleError } = await supabase
        .from("user_roles")
        .select("id")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (roleError) {
        if (!cancelled.current && !silent) setState("error");
        return;
      }
      if (!role) {
        if (!cancelled.current) setState("norole");
        return;
      }

      const { data: assurance } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (assurance?.currentLevel !== "aal2") {
        if (!cancelled.current) setState("nomfa");
        return;
      }

      const { error: geoError } = await supabase.functions.invoke("admin-geo-check", { body: {} });
      if (!cancelled.current) {
        if (!geoError) setState("ok");
        else if (!silent) setState("error");
      }
    } catch {
      if (!cancelled.current && !silent) setState("error");
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    cancelled.current = false;
    void check();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // Never run Supabase calls inside the callback itself (deadlocks the client).
      if (event === "SIGNED_OUT") {
        setState("noauth");
        return;
      }
      if (event === "SIGNED_IN" || event === "MFA_CHALLENGE_VERIFIED") {
        setTimeout(() => void check(), 0);
      }
      // TOKEN_REFRESHED / USER_UPDATED: keep the current view, re-validate quietly.
      if (event === "TOKEN_REFRESHED") {
        setTimeout(() => void check(true), 0);
      }
    });

    // Re-validate quietly when the tab becomes visible again after sleep.
    const onVisible = () => {
      if (document.visibilityState === "visible") void check(true);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled.current = true;
      document.removeEventListener("visibilitychange", onVisible);
      subscription.unsubscribe();
    };
  }, [check]);

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <p className="text-muted-foreground">Laden…</p>
      </div>
    );
  }

  if (state === "noauth") {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
  }

  if (state === "nomfa") {
    return <Navigate to="/admin/sicherheit" state={{ from: location.pathname }} replace />;
  }

  if (state === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-muted/30 px-4 text-center">
        <h1 className="text-xl font-semibold">Verbindung unterbrochen</h1>
        <p className="text-muted-foreground max-w-md">
          Die Sitzung konnte gerade nicht geprüft werden. Bitte versuchen Sie es erneut.
        </p>
        <div className="flex gap-2">
          <Button onClick={() => { setState("loading"); void check(); }}>Erneut versuchen</Button>
          <Button variant="outline" onClick={() => supabase.auth.signOut()}>Abmelden</Button>
        </div>
      </div>
    );
  }

  if (state === "norole") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-muted/30 px-4 text-center">
        <h1 className="text-xl font-semibold">Kein Zugriff</h1>
        <p className="text-muted-foreground max-w-md">
          Dieses Konto hat keine Administratorrechte. Bitte melden Sie sich mit dem Administratorkonto an.
        </p>
        <Button variant="outline" onClick={() => supabase.auth.signOut()}>
          Abmelden
        </Button>
      </div>
    );
  }

  const navItems = [
    { to: "/admin", label: "Übersicht", icon: LayoutDashboard },
    { to: "/admin/kunden", label: "Kunden", icon: Users },
    { to: "/admin/projekte", label: "Projekte", icon: FolderKanban },
    { to: "/admin/auftraege", label: "Aufträge", icon: ClipboardList },
    { to: "/admin/lexware", label: "Lexware", icon: Link2 },
    { to: "/admin/konto", label: "Sicherheit", icon: ShieldCheck },
  ];

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="sticky top-0 z-40 border-b bg-background">
        <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-3">
          <Link to="/admin" className="mr-4 flex items-center gap-2 font-semibold tracking-tight">
            <FileText className="h-5 w-5 text-primary" />
            <span className="hidden sm:inline">FACHSPRECHPARTNER · Leistungsnachweise</span>
            <span className="sm:hidden">LNW</span>
          </Link>
          <nav className="flex flex-1 items-center gap-1">
            {navItems.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link key={item.to} to={item.to}>
                  <Button variant={active ? "secondary" : "ghost"} size="sm" className="gap-2">
                    <item.icon className="h-4 w-4" />
                    <span className="hidden md:inline">{item.label}</span>
                  </Button>
                </Link>
              );
            })}
          </nav>
          <Button variant="ghost" size="sm" className="gap-2" onClick={() => supabase.auth.signOut()}>
            <LogOut className="h-4 w-4" />
            <span className="hidden md:inline">Abmelden</span>
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8">{children}</main>
    </div>
  );
};

export default AdminGuard;
